import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { CANDLE_WINDOW } from '../../constants';

import { DragState, HandleProps, RangeSelectorProps, RangeValues } from './types';

import cn from './NavigatorRange.module.scss';

const LeftHandle: React.FC<HandleProps> = React.memo(({ onDragStart, position }) => (
  <div
    role="slider"
    aria-label="left handle"
    aria-valuemin={0}
    aria-valuemax={100}
    tabIndex={0}
    className={classNames(cn.handle, cn.left)}
    style={{ left: position }}
    onMouseDown={(e) => onDragStart(e, 'left')}
    onTouchStart={(e) => onDragStart(e, 'left')}
  >
    <div className={cn.top} />
    <div className={cn.mid} />
    <div className={cn.bottom} />
  </div>
));

const RightHandle: React.FC<HandleProps> = React.memo(({ onDragStart, position }) => (
  <div
    role="slider"
    aria-label="right handle"
    aria-valuemin={0}
    aria-valuemax={100}
    tabIndex={0}
    className={classNames(cn.handle, cn.right)}
    style={{ right: `calc(100% - ${position})` }}
    onMouseDown={(e) => onDragStart(e, 'right')}
    onTouchStart={(e) => onDragStart(e, 'right')}
  >
    <div className={cn.top} />
    <div className={cn.mid} />
    <div className={cn.bottom} />
  </div>
));

export const RangeSelector: React.FC<RangeSelectorProps> = ({
  min = 0,
  max = CANDLE_WINDOW,
  step = 1,
  start = 0,
  end = CANDLE_WINDOW,
  onChange = () => {},
}) => {
  const [values, setValues] = useState<RangeValues>({ start, end });
  const [dragState, setDragState] = useState<DragState>({
    type: null,
    startPos: null,
    initialValues: null,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const windowSize = values.end - values.start;

  const positions = useMemo(
    () => ({
      left: `${((values.start - min) / (max - min)) * 100}%`,
      right: `${((values.end - min) / (max - min)) * 100}%`,
      width: `${(windowSize / (max - min)) * 100}%`,
    }),
    [values.start, values.end, min, max, windowSize]
  );

  useEffect(() => {
    if (values.start !== start || values.end !== end) {
      setValues({ start, end });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  useEffect(() => {
    if (values.start < min || values.end > max) {
      setValues((prev) => ({ start: Math.max(min, prev.start), end: Math.min(max, prev.end) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max]);

  const calculateValue = useCallback(
    (clientX: number, startX: number, startVal: number): number => {
      if (!containerRef.current) return startVal;

      const containerWidth = containerRef.current.offsetWidth;
      const range = max - min;
      const pixelsPerUnit = containerWidth / range;
      const diffX = clientX - startX;
      return Math.round(startVal + diffX / pixelsPerUnit);
    },
    [max, min]
  );

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent, type: DragState['type']) => {
      e.preventDefault();

      const startPos = 'touches' in e ? e.touches[0].clientX : e.clientX;
      setDragState({ type, startPos, initialValues: values });
    },
    [values]
  );

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!dragState.type || !dragState.startPos || !dragState.initialValues) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const newValues = { ...values };

      switch (dragState.type) {
        case 'left': {
          newValues.start = Math.max(
            min,
            Math.min(
              calculateValue(clientX, dragState.startPos, dragState.initialValues.start),
              values.end - step
            )
          );
          break;
        }
        case 'right': {
          newValues.end = Math.min(
            max,
            Math.max(
              calculateValue(clientX, dragState.startPos, dragState.initialValues.end),
              values.start + step
            )
          );
          break;
        }
        case 'middle': {
          const valueDiff = calculateValue(clientX, dragState.startPos, 0);
          const newStart = Math.max(
            min,
            Math.min(dragState.initialValues.start + valueDiff, max - windowSize)
          );
          const newEnd = newStart + windowSize;

          if (newStart >= min && newEnd <= max) {
            newValues.start = newStart;
            newValues.end = newEnd;
          }
          break;
        }
        default: {
          return;
        }
      }

      if (newValues.start !== values.start || newValues.end !== values.end) {
        setValues(newValues);
        onChange(newValues);
      }
    },
    [dragState, min, max, step, values, windowSize, calculateValue, onChange]
  );

  const handleDragEnd = useCallback(() => {
    setDragState({ type: null, startPos: null, initialValues: null });
  }, []);

  const dragHandlers = useMemo(
    () => ({ handleDragStart, handleDragMove, handleDragEnd }),
    [handleDragStart, handleDragMove, handleDragEnd]
  );

  useEffect((): (() => void) | undefined => {
    if (dragState.type) {
      window.addEventListener('mousemove', dragHandlers.handleDragMove, { passive: false });
      window.addEventListener('mouseup', dragHandlers.handleDragEnd);
      window.addEventListener('touchmove', dragHandlers.handleDragMove, { passive: false });
      window.addEventListener('touchend', dragHandlers.handleDragEnd);

      return () => {
        window.removeEventListener('mousemove', dragHandlers.handleDragMove);
        window.removeEventListener('mouseup', dragHandlers.handleDragEnd);
        window.removeEventListener('touchmove', dragHandlers.handleDragMove);
        window.removeEventListener('touchend', dragHandlers.handleDragEnd);
      };
    }
    return undefined;
  }, [dragState.type, dragHandlers]);

  if (min >= max) {
    console.error('min must be less than max');
    return null;
  }

  if (step <= 0) {
    console.error('step must be greater than 0');
    return null;
  }

  return (
    <div className={cn.container} ref={containerRef} role="group" aria-label="selector">
      <div className={classNames(cn.overlay, cn.left)} style={{ width: positions.left }} />
      <div
        className={classNames(cn.overlay, cn.right)}
        style={{ width: `calc(100% - ${positions.right})` }}
      />
      <LeftHandle position={positions.left} onDragStart={dragHandlers.handleDragStart} />
      <div
        className={cn.middle}
        style={{ left: positions.left, width: positions.width }}
        onMouseDown={(e) => dragHandlers.handleDragStart(e, 'middle')}
        onTouchStart={(e) => dragHandlers.handleDragStart(e, 'middle')}
      />
      <RightHandle position={positions.right} onDragStart={dragHandlers.handleDragStart} />
    </div>
  );
};
